import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  ConfiguredChannel,
  ResendType,
  ChannelsCascadeSettings,
  StartSendType,
  TimeOffsetType,
  AllowedTemplateOverrides,
  ChannelUIds,
  ChannelNotificationSettings
} from '../../../store/communications/communications.model';
import produce from 'immer';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import { ChannelSelectionService } from '../../../services/channel-selection-service';

@Component({
    selector: 'topo-template-channels-selection',
    templateUrl: './channels-selection.component.html',
    styleUrls: ['./channels-selection.component.scss'],
    standalone: false
})
export class TemplateChannelsSelection implements OnInit, OnDestroy {
  _channelsCascadeSettings: ChannelsCascadeSettings;
  @Input() set channelsCascadeSettings(value: ChannelsCascadeSettings) {
    this._channelsCascadeSettings = value;
    this.calculateChannelWidth(this._channelsCascadeSettings);
  }
  @Input() set allowedChanges(value: AllowedTemplateOverrides) {
    this.canChangeChannels = (value & AllowedTemplateOverrides.Channels) === AllowedTemplateOverrides.Channels;
    this.canChangeCascade = (value & AllowedTemplateOverrides.Cascade) === AllowedTemplateOverrides.Cascade;
    this.canChangeSendTime = (value & AllowedTemplateOverrides.SendTime) === AllowedTemplateOverrides.SendTime;
    this.canChangeResendTime = (value & AllowedTemplateOverrides.ResendTime) === AllowedTemplateOverrides.ResendTime;
    this.canChangeExpiration = (value & AllowedTemplateOverrides.Expiration) === AllowedTemplateOverrides.Expiration;
  }
  @Input() responseRequired: boolean;
  @Input() readonlyMode: boolean;
  @Input() summaryMode: boolean;
  @Input() canSkip: boolean;
  @Input() skippedChannels: string[];
  @Input() viewOnly: boolean;
  @Input() set width(value: number) {
    this._width = value;
    if (!!value) {
      this.calculateChannelWidth(this._channelsCascadeSettings);
    }
  }
  get width(): number {
    return this._width;
  }
  @Output() changed = new EventEmitter<ChannelsCascadeSettings>();
  @Output() skippedChanged = new EventEmitter<string[]>();
  @Output() showAdvacedSchedules = new EventEmitter();

  _width: number;
  canChangeChannels: boolean;
  canChangeCascade: boolean;
  canChangeSendTime: boolean;
  canChangeResendTime: boolean;
  canChangeExpiration: boolean;
  channelWidth: string;
  showConfirmAdvanced: boolean;

  TimeOffsetType = TimeOffsetType;

  get channelsCascadeSettings(): ChannelsCascadeSettings {
    return this._channelsCascadeSettings;
  }

  constructor(
    private translateService: TranslateService,
    private channelSelectionService: ChannelSelectionService
  ) {}

  ngOnInit(): void {
    if (!!!this.channelSelectionService.availableChannels) {
      this.channelSelectionService.loadAvailableChannels(true);
    }
  }

  ngOnDestroy(): void {}

  startTimeChanged(channelUId: string, data: any): void {
    const newSettings = produce(this._channelsCascadeSettings, draftState => {
      if (!!channelUId) {
        const notification = draftState?.channelNotifications?.find(n => n.uId === channelUId);
        if (!!notification) {
          notification.startTimeMinutes = parseInt(data.srcElement?.value);
        }
      } else {
        draftState?.channelNotifications?.forEach(c => {
          if (!!c) { 
            c.startTimeMinutes = parseInt(data.srcElement?.value);
          }
        });
      }
    });

    this.changed.emit(newSettings);
  }

  resendTimeChanged(channelUId: string, data: any): void {
    const newSettings = produce(this._channelsCascadeSettings, draftState => {
      if (!!channelUId) {
        const notification = draftState?.channelNotifications?.find(n => n.uId === channelUId);
        if (!!notification) {
          notification.resendTimeMinutes = parseInt(data.srcElement?.value);
        }
      } else {
        draftState?.channelNotifications?.forEach(c => {
          if (!!c) {
            c.resendTimeMinutes = parseInt(data.srcElement?.value);
          }
        });
      }
    });

    this.changed.emit(newSettings);
  }

  stopTimeChanged(channelUId: string, data: any): void {
    const newSettings = produce(this._channelsCascadeSettings, draftState => {
      if (!!channelUId) {
        const notification = draftState?.channelNotifications?.find(n => n.uId === channelUId);
        if (!!notification) {
          notification.stopTimeMinutes = parseInt(data.srcElement?.value);
        }
      } else {
        draftState?.channelNotifications?.forEach(c => {
          c.stopTimeMinutes = parseInt(data.srcElement?.value);
        });
      }
    });

    this.changed.emit(newSettings);
  }

  setAdvancedMode(event: any): void {
    if (this._channelsCascadeSettings?.advancedMode === true && this._channelsCascadeSettings?.channelNotifications?.length > 0) {
      this.showConfirmAdvanced = true;
      return;
    } else {
      this.toggleAdvanced();
    }
  }

  getStopTimes(settings: ChannelNotificationSettings): any[] {
    const stopTimes = this.channelSelectionService.stopTimes.filter(
      s => s.value > settings.startTimeMinutes && s.value > settings.resendTimeMinutes
    );
    if (!stopTimes.find(s => s.value === settings.stopTimeMinutes)) {
      settings.stopTimeMinutes = stopTimes[0].value;
    }

    return stopTimes;
  }

  private toggleAdvanced(): void {
    const newSettings = produce(this._channelsCascadeSettings, draftState => {
      draftState.advancedMode = !draftState.advancedMode;
      if (draftState.advancedMode === false) {
        const notification = draftState.channelNotifications.find(n => !!n);
        if (!!notification) {
          draftState.channelNotifications?.forEach(n => {
            if (!!n) {
              n.startTimeMinutes = notification.startTimeMinutes;
              n.resendTimeMinutes = notification.resendTimeMinutes;
              n.stopTimeMinutes = notification.stopTimeMinutes;
            }
          });
        }
      }
    });

    this.calculateChannelWidth(newSettings);

    this.changed.emit(newSettings);
  }

  private calculateChannelWidth(settings: ChannelsCascadeSettings): void {
    if (settings?.advancedMode === true) {
      this.channelWidth = '250px';
    } else {
      this.channelWidth = `${this._width - 32}px`;
    }
  }

  onConfirmAdvancedClose(response: boolean): void {
    if (response) {
      this.toggleAdvanced();
    }
    this.showConfirmAdvanced = false;
  }

  get startTimes(): any[] {
    return this.channelSelectionService.startTimes;
  }

  get resendTimes(): any[] {
    return this.channelSelectionService.resendTimes;
  }

  get stopTimes(): any[] {
    return this.channelSelectionService.stopTimes;
  }

  getSelectedChannel(uid: string): ConfiguredChannel {
    const channel = this.channelSelectionService.availableChannels?.find(c => c.uId === uid);
    if (!!channel) {
      return {
        ...channel,
        icon: this.getChannelIcon(channel)
      } as ConfiguredChannel;
    } else {
      return null;
    }
  }

  selectedChannelChanged(uid: string, channel: ConfiguredChannel): void {
    if (!!!channel) {
      return;
    }
    const newSettings = produce(this._channelsCascadeSettings, draftState => {
      const nullIndex = draftState.channelNotifications.indexOf(null);
      if (nullIndex >= 0) {
        draftState.channelNotifications[nullIndex] = {
          uId: uuidv4(),
          configuredChannelUId: channel.uId,
          startTimeMinutes:
            this._channelsCascadeSettings.advancedMode === true
              ? 0
              : (this._channelsCascadeSettings?.channelNotifications[0]?.startTimeMinutes ?? 0),
          resendTimeMinutes:
            this._channelsCascadeSettings.advancedMode === true
              ? 0
              : (this._channelsCascadeSettings?.channelNotifications[0]?.resendTimeMinutes ?? 0),
          stopTimeMinutes:
            this._channelsCascadeSettings.advancedMode === true
              ? 15
              : (this._channelsCascadeSettings?.channelNotifications[0]?.stopTimeMinutes ?? 15)
        };
      } else {
        const replaceIndex = draftState.channelNotifications?.findIndex(c => c.uId === uid);
        if (replaceIndex >= 0) {
          draftState.channelNotifications[replaceIndex].configuredChannelUId = channel.uId;
        }
      }
    });

    this.changed.emit(newSettings);
  }

  addChannel(): void {
    const newSettings = produce(this._channelsCascadeSettings, draftState => {
      if (!!!draftState.channelNotifications) {
        draftState.channelNotifications = [];
      }
      if (!draftState.channelNotifications.includes(null)) {
        draftState.channelNotifications.push(null);
      }
    });

    this.changed.emit(newSettings);
  }

  deleteChannelClick(channelUId: string): void {
    const newSettings = produce(this._channelsCascadeSettings, draftState => {
      const searchFn = channelUId === null
        ? (c: ChannelNotificationSettings) => c === null
        : (c: ChannelNotificationSettings) => c.uId === channelUId;

      const channelIndex = draftState.channelNotifications?.findIndex(searchFn);

      if (channelIndex >= 0) {
        draftState.channelNotifications.splice(channelIndex, 1);
      }
    });

    this.changed.emit(newSettings);
  }

  getAvailableChannelsForSelection(selectedUId: string, selectedChannelUId: string): ConfiguredChannel[] {
    const channels = this.channelSelectionService.availableChannels?.filter(
      c =>
        c.uId === selectedChannelUId || !this.channelsCascadeSettings?.channelNotifications?.some(cu => cu?.configuredChannelUId === c.uId)
    );

    return channels?.map(c => {
      return { ...c, icon: !!c.uId ? this.getChannelIcon(c) : null };
    });
  }

  getChannelIcon(channel: ConfiguredChannel): string {
    return this.channelSelectionService.getChannelIcon(channel.channelUId);
  }

  hasActiveChannels(): boolean {
    return true;
  }

  getAvailableChannels(): ConfiguredChannel[] {
    return this.channelSelectionService.availableChannels;
  }

  isValidChannel(channelUId: string): boolean {
    return this.channelSelectionService.availableChannels?.some(c => c.uId === channelUId);
  }

  getChannelActive(uid: string): boolean {
    return !this.skippedChannels?.find(c => c == uid);
  }

  setChannelActive(uid: string, event: any): void {
    let isActive = this.getChannelActive(uid);

    isActive = !isActive;

    if (!!!this.skippedChannels) {
      this.skippedChannels = [];
    }

    const newSettings = produce(this.skippedChannels, draftState => {
      if (isActive === true) {
        const index = draftState.indexOf(uid);
        if (index >= 0) {
          draftState.splice(index, 1);
        }
      } else {
        draftState.push(uid);
      }
    });

    this.skippedChanged.emit(newSettings);
  }

  getChannelSettingsDisplay(settings: ChannelNotificationSettings): string {
    return this.channelSelectionService.getChannelSettingsDisplay(settings);
  }

  showAdvancedScheduling(): void {
    this.showAdvacedSchedules.emit();
  }
}
